<template>
  <div>
    <section
      class="header py-2"
      :class="{'bg-sky-400': !user, 'bg-stone-800': user}"
    >
      <div class="container">
        <div class="flex">
          <div
            class="flex-none"
            :class="{'hidden md:inline-block': searching}"
          >
            <div class="flex">
              <Link :href="route('frontend.index')">
                <img
                  src="/img/logo.svg"
                  class="w-[100px]"
                >
              </Link>
            </div>
          </div>

          <div class="grow">
            <div
              class="flex justify-end"
              :class="{'w-full': searching}"
            >
              <a
                v-if="!searching"
                href="javascript:void(0);"
                class="py-2.5 px-4 rounded mr-2 bg-sky-600 hover:bg-sky-700"
                @click="toggleSearch"
              >
                <i class="far fa-search relative top-[.2rem] text-white text-xl" />
                <span class="text-sm hidden lg:inline font-bold text-white ml-4 uppercase mt-2">Zoeken</span>
              </a>
              <form
                v-if="searching"
                id="search"
                class="relative mr-1"
                :class="{'w-full md:w-auto' : searching}"
                @submit.prevent="search"
              >
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="h-5 w-5 text-gray-300 fa fa-search" />
                </div>
                <input
                  v-model="searchQuery"
                  :disabled="searchLoading"
                  type="text"
                  name="query"
                  class="block w-full md:w-80 p-3 pl-10 text-sm border-0 rounded-md"
                  :class="{'bg-gray-100': searchLoading, 'focus:ring-sky-500': !user, 'focus:ring-red-500': user}"
                  placeholder="Zoekterm"
                  required
                >
              </form>
              <a
                v-if="searchLoading"
                href="javascript:void(0);"
                class="py-2.5 px-4 rounded mr-0 md:mr-2 bg-sky-600 hover:bg-sky-700"
              >
                <i class="fa fa-spinner fa-spin relative top-[.2rem] text-white text-xl" />
              </a>
              <button
                v-else-if="searching"
                form="search"
                class="py-2.5 px-4 rounded mr-0 md:mr-2 bg-sky-600 hover:bg-sky-700"
              >
                <i class="far fa-search relative top-[.2rem] text-white text-xl" />
              </button>

              <template v-if="loggedIn">
                <a
                  :href="route('frontend.favorites')"
                  class="py-2.5 px-4 rounded mr-2 bg-sky-600 hover:bg-sky-700"
                  :class="{'hidden md:inline-block': searching}"
                >
                  <i class="far fa fa-star relative top-[.2rem] text-white text-xl" />
                  <span class="text-sm hidden lg:inline font-bold text-white ml-4 uppercase mt-2">
                    Favorieten ({{ $page.props.customer.number_of_favorites }})
                  </span>
                </a>

                <a
                  :href="route('profile.index')"
                  class="py-2.5 px-4 rounded bg-sky-600 hover:bg-sky-700"
                  :class="{'hidden md:inline-block': searching}"
                >
                  <i class="far fa-user relative top-[.2rem] text-white text-xl" />
                  <span
                    class="text-sm hidden lg:inline font-bold text-white ml-4 uppercase mt-2"
                    v-text="translations.account_button"
                  />
                </a>
              </template>
              <template v-else>
                <a
                  href="javascript:void(0);"
                  class="py-2.5 px-4 rounded bg-sky-600 hover:bg-sky-700"
                  :class="{'hidden md:inline-block': searching}"
                  @click="login"
                >
                  <i class="far fa-user relative top-[.2rem] text-white text-xl" />
                  <span
                    class="text-sm hidden lg:inline font-bold text-white ml-4 uppercase mt-2"
                    v-text="translations.login_button"
                  />
                </a>
              </template>

              <a
                id="cart"
                :href="route('cart.index')"
                class="ml-4 flex relative pr-2.5"
                :class="{'hidden md:flex': searching}"
              >
                <img src="/img/shopping-cart.svg">
                <div
                  class="bg-yellow-400 w-5 h-5 text-black absolute rounded-full right-0 text-center font-semibold text-xs leading-[1.4rem]"
                  v-text="cartItems"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="info-bar bg-sky-500">
      <div class="container">
        <div class="flex">
          <div class="w-1/2 lg:w-3/5 pt-5 pb-4 text-sm text-white uppercase font-medium leading-6">
            <span class="hidden xl:inline">
              Direct contact: <a
                :href="`mailto:${translations.email}`"
                class="hover:underline"
                v-text="translations.email"
              /> of bezoek onze
            </span>
            <a
              :href="route('contact')"
              class="underline hover:no-underline inline"
              v-text="translations.service"
            />
          </div>

          <div
            v-if="reviews.average_rating"
            class="pt-5 pb-4 w-1/2 mt-0.5 lg:w-2/5 "
          >
            <a
              :href="reviews.place"
              target="_blank"
            >
              <div class="flex justify-end space-x-1 text-sm text-white uppercase font-medium">
                <span
                  class="hidden md:inline mr-1"
                  v-text="translations.reviews"
                />
                <Stars
                  class="space-x-1 flex"
                  star-class="h-4"
                  :rating="reviews.average_rating"
                  full-star="/img/star.svg"
                  half-star="/img/star-half.svg"
                >
                  <span
                    class="inline-block pl-1"
                    v-text="`${reviews.average_rating}/10`"
                  />
                </Stars>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {Link} from '@inertiajs/vue2';
import Stars from './Stars';
import Counter from '../Mixins/Counter';

export default {
  components: {Stars, Link},

  mixins: [Counter],

  props: {
    page: {
      type: String,
      required: false,
      default: null,
    },
  },

  data () {
    let flashProductsHeaderText = '';

    if (this.$page.props.flash_sales.new.delivered_next_day) {
      const parts = this.$page.props.flash_sales.new.ordered_before.split(':');
      const orderedBefore = [parts[0], parts[1]].join(':');

      flashProductsHeaderText = `&#128230; Voor ${orderedBefore} besteld, morgen in huis`;
    }

    return {
      user: this.$page.props.user || null,
      loggedIn: this.$page.props.customer.authenticated,
      cartItems: this.$page.props.cart.items,
      translations: this.$page.props.translations.header,
      reviews: this.$page.props.reviews,

      textInterval: null,

      flashProductsHeaderText,
      dailyDiscountsHeaderText: '',
      auctionsHeaderText: '',

      searching: false,
      searchLoading: false,
      searchQuery: null,
    };
  },

  mounted () {
    if (this.$page.props.cart.id) {
      this.$echo.channel('carts.' + this.$page.props.cart.id)
          .listen('CartWasUpdated', (event) => this.updateCart(event));
    }
  },

  destroyed () {
    if (this.$page.props.cart.id) {
      this.$echo.leaveChannel('carts.' + this.$page.props.cart.id);
    }
  },

  methods: {
    toggleSearch() {
      this.searching = !this.searching;

      if (this.searching) {
        this.$nextTick(() => {
          const searchInput = document.querySelector('input[name="query"]');
          if (searchInput) {
            searchInput.focus();
          }
        });
      }
    },

    search() {
      if (this.searchLoading) {
        return;
      }

      this.searchLoading = true;
      this.$inertia.get(this.route('frontend.search', {query: this.searchQuery}), {}, {
        onFinish: () => {
          this.searchLoading = false;
          this.searching = false;
        },
      });
    },

    login () {
      this.$eventHub.$emit('login');
    },

    updateCart (event) {
      this.cartItems = event.cart.item_count;
    },
  },
};
</script>
